import React, { useCallback, useEffect, useMemo, useState } from "react";
import { inject, observer } from "mobx-react";
import { ColumnType } from "antd/es/table/interface";
import DeclarantStore from "../stores/DeclarantStore";
import Summary from "../components/declarants/summary";
import ImportDeclarants from "../components/declarants/importDeclarants";
import DateRange from "../components/declarants/dateRange";
import CreateDeclarant from "../components/declarants/createDeclarant";
import Search from "../components/declarants/search";
import Download from "../components/declarants/download";
import Settings from "../components/declarants/Settings";
import { DateRangeDate, IObjectMap } from "../interfaces/common";
import COLUMNS from "../components/tables/declarantTable/config";
import {
  DECLARANT_ACTIVE_COLUMNS,
  DECLARANT_ORDER_COLUMNS,
} from "../constants/settings";
import updateSortingAndFiltersOfColumns from "../helpers/filters";
import CalculateClientSum from "../components/buttons/calculateClientsFeeButton";
import SettingsStore from "../stores/SettingsStore";
import UserStore from "../stores/UserStore";
import CollapseHeader from "../components/CollapseHeader";
import Card from "../components/Card/Card";
import TableWrapper from "../components/declarants/tableWrapper";
import { Declarant } from "../models/Declarant";
import ReordableVirtualTable from "../components/common/Table/ReordableVirtualTable";
import eventEmitter from "../services/EventEmitter";
import RefreshButton from "../components/buttons/RefreshButton";
import { EXPORT_TO_EXCEL } from "constants/translates";
import ActionButton from "components/declarants/groupButton";
import TwsSyncContainer from "../components/declarants/twsSyncContainer";
import AssignClientContainer from "../components/declarants/assignClientContainer";

const declarantStore = new DeclarantStore();
let isLoaded = false;

const onLoadHandler = (
  parameters?: IObjectMap,
  forceUpdate?: boolean
): void => {
  declarantStore.load(parameters || {}, forceUpdate);
};

const DashboardPage: React.FC<{
  settingsStore: SettingsStore;
  userStore: UserStore;
}> = ({ settingsStore, userStore }) => {
  const downloadIsDisabled = !(declarantStore?.data?.length > 0);

  useEffect(() => {
    if (isLoaded) {
      eventEmitter.emit("table:re-render", null);
      return;
    }

    declarantStore.load({}, true);
    isLoaded = true;
  }, []);

  const [height, setHeight] = useState(window.innerHeight);

  const changePositionHandler = useCallback(({ top }) => {
    setHeight(window.innerHeight - top - 72);
  }, []);

  useEffect(() => {
    settingsStore.setColumns(
      updateSortingAndFiltersOfColumns(
        userStore.authorizedOnly(COLUMNS(declarantStore)),
        declarantStore.parameters as IObjectMap
      ),
      DECLARANT_ORDER_COLUMNS,
      DECLARANT_ACTIVE_COLUMNS
    );
  }, [
    settingsStore,
    settingsStore.settings,
    userStore,
    userStore.user,
    settingsStore.isEnabledReorder,
    declarantStore.parameters,
  ]);

  const changeHandler = useCallback((values) => {
    declarantStore.changeSelectedRows(values);
  }, []);

  const columns = useMemo(() => {
    if (settingsStore.isEnabledScrollbar) {
      return settingsStore.activeColumns;
    }

    return settingsStore.activeColumns.map((column) => ({
      ...column,
      width: undefined,
    }));
  }, [settingsStore.activeColumns, settingsStore.isEnabledScrollbar]);

  const downloadExcelFile = () => {
    const activeColumnKey = settingsStore.columns
      .filter((column) => column.isActive && column.key !== "actions")
      .map((column) => {
        return { [column.key]: column.title };
      });

    declarantStore.downloadExcel({
      activeColumn: activeColumnKey,
      ...declarantStore.parameters,
    });
  };

  return (
    <>
      <CollapseHeader>
        <div className={"column-container"}>
          <div className={"row-container w-full"}>
            <DateRange
              store={declarantStore}
              startDate={declarantStore.parameters.start_date as DateRangeDate}
              endDate={declarantStore.parameters.end_date as DateRangeDate}
            />
            <Search store={declarantStore} />
          </div>
          <div className="row-container">
            <TwsSyncContainer store={declarantStore} />
            <AssignClientContainer
              store={declarantStore}
              clients={settingsStore.clients || []}
            />
          </div>
        </div>
        <Summary store={declarantStore} />
      </CollapseHeader>
      <Card>
        <Card.Header>
          <ImportDeclarants store={declarantStore} />
          <Download store={declarantStore} />
          <ActionButton
            onClick={downloadExcelFile}
            disabled={downloadIsDisabled || declarantStore.isLoadingButton}
            text={EXPORT_TO_EXCEL}
            loading={declarantStore.isLoadingButton}
          />
          <CreateDeclarant
            clients={settingsStore.clients || []}
            noClient={settingsStore.noClient}
            departments={settingsStore.departments || []}
          />
          <CalculateClientSum />
          <RefreshButton refresh={declarantStore} />
          <Settings reorderable />
        </Card.Header>
        <TableWrapper
          className="custom-virtual-table"
          enableScrollBar={settingsStore.isEnabledScrollbar}
          changePositionHandler={changePositionHandler}
        >
          <ReordableVirtualTable
            settingsStore={settingsStore}
            dataSource={declarantStore.data}
            loading={
              declarantStore.isLoadingTableData ||
              declarantStore.isUploadingXmlFile
            }
            scroll={{ y: height, x: "100vw" }}
            columns={columns as ColumnType<Declarant>[]}
            rowClassName={(record: Declarant) => {
              let classes = "";

              if (record.exposed) {
                classes += " exposed-row";
              }

              if (declarantStore.selectedRows.includes(record.id as number)) {
                classes += " selected-row";
              }

              return classes.trim();
            }}
            selected={declarantStore.selectedRows}
            changeSelectHandler={changeHandler}
            loadMoreHandler={onLoadHandler}
            hasMoreItems={declarantStore.hasMoreItems}
          />
        </TableWrapper>
      </Card>
    </>
  );
};

export default inject(({ store }) => ({
  settingsStore: store.settingsStore,
  userStore: store.userStore,
}))(observer(DashboardPage));
